/**
 * PDP sticky anchor nav — deferred boot with cached section targets.
 * Active tab uses IntersectionObserver when possible; stuck bar still tracks scroll.
 */
(function () {
  function sectionKeyFrom(raw) {
    var value = String(raw || '').trim();
    if (!value) return '';
    value = value.replace(/^#/, '');
    var dunder = value.lastIndexOf('__');
    if (dunder !== -1) return value.slice(dunder + 2);
    return value.replace(/^shopify-section-/, '');
  }

  function resolveTargetUncached(raw) {
    if (!raw) return null;
    var id = String(raw).trim().replace(/^#/, '');
    if (!id) return null;

    var exact =
      document.getElementById(id) ||
      document.getElementById('shopify-section-' + id) ||
      document.querySelector('[data-section-id="' + id.replace(/"/g, '\\"') + '"]');
    if (exact) return exact.closest ? exact.closest('.shopify-section') || exact : exact;

    var key = sectionKeyFrom(id);
    if (!key) return null;

    var suffix = '__' + key;
    var sections = document.querySelectorAll('.shopify-section[id]');
    for (var i = 0; i < sections.length; i++) {
      var sid = sections[i].id || '';
      if (sid === key || sid === 'shopify-section-' + key || sid.slice(-suffix.length) === suffix) {
        return sections[i];
      }
    }

    var marked = document.querySelectorAll('[data-section-id]');
    for (var j = 0; j < marked.length; j++) {
      var dataId = marked[j].getAttribute('data-section-id') || '';
      if (dataId === key || dataId.slice(-suffix.length) === suffix) {
        return marked[j].closest
          ? marked[j].closest('.shopify-section') || marked[j]
          : marked[j];
      }
    }

    return null;
  }

  function initAnchorNav(root) {
    if (!root || root.getAttribute('data-pdp-anchor-ready') === 'true') return;
    root.setAttribute('data-pdp-anchor-ready', 'true');

    var bar = root.querySelector('[data-anchor-nav-bar]');
    var inner = root.querySelector('[data-anchor-nav-inner]');
    var prevArrow = root.querySelector('[data-anchor-nav-prev]');
    var nextArrow = root.querySelector('[data-anchor-nav-next]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-anchor-target]'));
    if (!tabs.length || !bar || !inner) return;

    var isStuck = false;
    var wasDragging = false;
    var targetCache = Object.create(null);
    var tabByTarget = [];

    function resolveTarget(raw) {
      var key = String(raw || '').trim();
      if (!key) return null;
      if (Object.prototype.hasOwnProperty.call(targetCache, key)) {
        var cached = targetCache[key];
        if (cached && document.contains(cached)) return cached;
      }
      var found = resolveTargetUncached(key);
      targetCache[key] = found;
      return found;
    }

    function clearTargetCache() {
      targetCache = Object.create(null);
      tabByTarget = [];
      tabs.forEach(function (tab) {
        var el = resolveTarget(tab.getAttribute('data-anchor-target'));
        if (el) tabByTarget.push({ tab: tab, target: el });
      });
    }

    function headerOffset() {
      var header = document.getElementById('SiteHeader');
      if (header) {
        var rect = header.getBoundingClientRect();
        if (rect.height > 0) return Math.max(rect.bottom, 0);
      }
      var value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }

    function scrollOffset() {
      return headerOffset() + bar.offsetHeight + 12;
    }

    function updateStuckState() {
      var offset = headerOffset();
      var shouldStick = root.getBoundingClientRect().top <= offset;

      if (shouldStick) {
        if (!isStuck) {
          root.style.height = bar.offsetHeight + 'px';
          root.classList.add('is-stuck');
          isStuck = true;
        }
        bar.style.top = offset + 'px';
      } else if (isStuck) {
        root.classList.remove('is-stuck');
        root.style.height = '';
        bar.style.top = '';
        isStuck = false;
      }
    }

    function setActive(activeTab) {
      tabs.forEach(function (tab) {
        var on = tab === activeTab;
        tab.classList.toggle('is-active', on);
        if (on) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      if (!activeTab) return;

      var tr = activeTab.getBoundingClientRect();
      var ir = inner.getBoundingClientRect();
      if (tr.left < ir.left || tr.right > ir.right) {
        inner.scrollTo({ left: activeTab.offsetLeft - 20, behavior: 'smooth' });
      }
    }

    function updateActiveFromScroll() {
      var pos = scrollOffset() + 2;
      var active = null;
      for (var i = 0; i < tabByTarget.length; i++) {
        var entry = tabByTarget[i];
        if (entry.target.getBoundingClientRect().top <= pos) {
          active = entry.tab;
        }
      }
      setActive(active || tabs[0]);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        updateStuckState();
        updateActiveFromScroll();
        ticking = false;
      });
    }

    // Prefer IntersectionObserver for active tab; keep scroll fallback for sticky offset.
    var sectionObserver = null;
    if ('IntersectionObserver' in window) {
      sectionObserver = new IntersectionObserver(
        function () {
          updateActiveFromScroll();
        },
        {
          root: null,
          rootMargin: '-20% 0px -60% 0px',
          threshold: [0, 0.25, 0.5, 0.75, 1],
        }
      );
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (event) {
        if (wasDragging) {
          event.preventDefault();
          return;
        }
        var target = resolveTarget(tab.getAttribute('data-anchor-target'));
        if (!target) return;
        var top = target.getBoundingClientRect().top + window.pageYOffset - scrollOffset();
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      });
    });

    function updateArrowState() {
      if (!prevArrow || !nextArrow) return;
      var maxScroll = inner.scrollWidth - inner.clientWidth;
      prevArrow.disabled = inner.scrollLeft <= 1;
      nextArrow.disabled = inner.scrollLeft >= maxScroll - 1;
    }

    function initSlider() {
      var overflowing = inner.scrollWidth > inner.clientWidth + 1;
      inner.classList.toggle('is-scrollable', overflowing);
      inner.classList.toggle('has-arrows', overflowing);
      if (prevArrow) prevArrow.hidden = !overflowing;
      if (nextArrow) nextArrow.hidden = !overflowing;
      if (overflowing) updateArrowState();
    }

    function scrollByStep(direction) {
      var step = Math.max(inner.clientWidth * 0.6, 120);
      inner.scrollTo({ left: inner.scrollLeft + step * direction, behavior: 'smooth' });
    }

    if (prevArrow) prevArrow.addEventListener('click', function () { scrollByStep(-1); });
    if (nextArrow) nextArrow.addEventListener('click', function () { scrollByStep(1); });
    inner.addEventListener('scroll', updateArrowState, { passive: true });

    function setupDrag() {
      var isDown = false;
      var startX = 0;
      var startScroll = 0;

      inner.addEventListener('mousedown', function (event) {
        if (!inner.classList.contains('is-scrollable')) return;
        isDown = true;
        wasDragging = false;
        startX = event.pageX;
        startScroll = inner.scrollLeft;
        inner.classList.add('is-dragging');
      });

      window.addEventListener('mousemove', function (event) {
        if (!isDown) return;
        var delta = event.pageX - startX;
        if (Math.abs(delta) > 4) wasDragging = true;
        inner.scrollLeft = startScroll - delta;
      });

      function stopDrag() {
        if (!isDown) return;
        isDown = false;
        inner.classList.remove('is-dragging');
        setTimeout(function () {
          wasDragging = false;
        }, 0);
      }

      window.addEventListener('mouseup', stopDrag);
      inner.addEventListener('mouseleave', stopDrag);
    }

    function observeTargets() {
      if (!sectionObserver) return;
      sectionObserver.disconnect();
      tabByTarget.forEach(function (entry) {
        sectionObserver.observe(entry.target);
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      if (isStuck) {
        root.classList.remove('is-stuck');
        root.style.height = '';
        isStuck = false;
      }
      clearTargetCache();
      observeTargets();
      initSlider();
      onScroll();
    });

    clearTargetCache();
    observeTargets();
    setupDrag();
    initSlider();
    onScroll();
  }

  function boot() {
    document.querySelectorAll('[data-pdp-anchor-nav]').forEach(initAnchorNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-pdp-anchor-nav]').forEach(function (root) {
      root.removeAttribute('data-pdp-anchor-ready');
    });
    boot();
  });
})();
